import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { ShieldCheck, Users, Dumbbell, AlertTriangle, ChevronDown, ChevronRight } from "lucide-react"
import { KpiCard } from "@/components/KpiCard"
import { getProgramIntegrity, type IntegrityUser } from "@/lib/api"

const RULE_LABELS: Record<string, string> = {
  missing_antagonist: "Missing opposite-pattern work",
  consecutive_strength_days: "Back-to-back lifting days",
  triple_strength_days: "3+ lifting days in a row",
  broken_alternation: "Broken emphasis rotation",
}

function UserRow({ user }: { user: IntegrityUser }) {
  const [open, setOpen] = useState(false)
  const clean = user.finding_count === 0
  return (
    <>
      <tr
        className={`border-b cursor-pointer hover:bg-muted/50 ${clean ? "" : "bg-red-50/40"}`}
        onClick={() => !clean && setOpen(!open)}
      >
        <td className="px-3 py-2 font-medium">
          <span className="inline-flex items-center gap-1">
            {!clean && (open ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />)}
            {user.name}
          </span>
        </td>
        <td className="px-3 py-2 capitalize">{user.archetype.replace("_", " ")}</td>
        <td className="px-3 py-2 text-center">{user.declared_strength_frequency || "—"}</td>
        <td className="px-3 py-2 text-center">{user.strength_sessions}</td>
        <td className="px-3 py-2 text-center">
          {clean ? (
            <span className="text-green-700 font-medium">Clean</span>
          ) : (
            <span className="text-red-700 font-semibold">{user.finding_count}</span>
          )}
        </td>
      </tr>
      {open && (
        <tr className="border-b bg-muted/30">
          <td colSpan={5} className="px-6 py-3">
            <ul className="space-y-1 text-sm">
              {user.findings.map((f, i) => (
                <li key={i} className="flex gap-2">
                  <span className="shrink-0 rounded bg-red-100 text-red-800 px-1.5 py-0.5 text-xs font-medium">
                    {RULE_LABELS[f.rule] ?? f.rule}
                  </span>
                  <span className="text-muted-foreground">
                    {f.date ? `${f.date} — ` : ""}{f.detail}
                  </span>
                </li>
              ))}
            </ul>
          </td>
        </tr>
      )}
    </>
  )
}

export function ProgramIntegrityPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["program-integrity"],
    queryFn: getProgramIntegrity,
  })

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        <span className="ml-3 text-muted-foreground">Checking every season against the rulebook…</span>
      </div>
    )
  }
  if (error || !data) {
    return (
      <div className="p-6 text-red-700">
        Failed to load the integrity report: {(error as Error)?.message}
      </div>
    )
  }

  const s = data.summary
  const pctVariant = s.users_with_findings_pct >= 40 ? "danger" : s.users_with_findings_pct > 0 ? "warning" : "success"

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ShieldCheck className="size-6" /> Program Integrity
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Every stored season, checked against the Magna Carta VI structural rules. Recomputed live on each page load.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          title="Users with rule violations"
          value={`${s.users_with_findings} of ${s.users_checked} (${s.users_with_findings_pct}%)`}
          icon={Users}
          variant={pctVariant}
          subtitle="Users whose current plan breaks ≥1 rule"
        />
        <KpiCard
          title="Sessions missing opposite-pattern work"
          value={`${s.antagonist_violation_pct}%`}
          icon={Dumbbell}
          variant={s.antagonist_violation_pct > 0 ? "warning" : "success"}
          subtitle={`Of ${s.scored_strength_sessions} emphasis-labeled strength sessions`}
        />
        <KpiCard
          title="Back-to-back lifting days"
          value={s.findings_by_rule.consecutive_strength_days ?? 0}
          icon={AlertTriangle}
          variant={(s.findings_by_rule.consecutive_strength_days ?? 0) > 0 ? "warning" : "success"}
          subtitle="Unsanctioned adjacent-day pairs (BB split excluded)"
        />
        <KpiCard
          title="3+ lifting days in a row"
          value={s.findings_by_rule.triple_strength_days ?? 0}
          icon={AlertTriangle}
          variant={(s.findings_by_rule.triple_strength_days ?? 0) > 0 ? "danger" : "success"}
          subtitle="Never allowed, any archetype"
        />
      </div>

      <div className="rounded-lg border bg-card">
        <div className="border-b px-4 py-3">
          <h2 className="font-semibold">Per-user breakdown</h2>
          <p className="text-xs text-muted-foreground">Click a flagged user to see each violation with its date.</p>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-muted-foreground">
              <th className="px-3 py-2 font-medium">User</th>
              <th className="px-3 py-2 font-medium">Archetype</th>
              <th className="px-3 py-2 font-medium text-center">Lifts/wk</th>
              <th className="px-3 py-2 font-medium text-center">Strength sessions</th>
              <th className="px-3 py-2 font-medium text-center">Violations</th>
            </tr>
          </thead>
          <tbody>
            {data.users.map((u) => (
              <UserRow key={u.user_id} user={u} />
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
        <p className="font-medium text-foreground mb-1">📖 How to read this dashboard</p>
        <p>
          This page re-runs the deterministic rule checker over every user's stored season on load — it is the
          "watch mode" instrument for the structural-validator rollout. "Missing opposite-pattern work" means an
          emphasis day (e.g. Push/Hinge) has no real dynamic exercise for the required opposite patterns — isometric
          holds like planks don't count. Back-to-back lifting days are exempt for Bodybuilders programmed at 4+
          lifts/week (their 2-on/1-off split is by design); 3-in-a-row is never allowed for anyone. Once generation
          enforcement ships, the top tiles should trend to zero for newly generated weeks; existing plans keep their
          historical violations until regenerated.
        </p>
      </div>
    </div>
  )
}
